import React, {useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "../../../../../shadCn/components/ui/dialog";
import {DetailSummaryButton} from "../../../../../components/detailLayout";
import {handleActiveToast} from "../../../../../components/sonner/ToastBasic";
import {apiLoading} from "../../../../../redux/slice/apiSlice";
import {saveSla, updateSla} from "../../../../../api/evaluation/SlaService";
import {
    applyWeightCalculations,
    buildSavePayload,
    computeEvaluationResult,
    computeWeightSum,
    handleSlaInputChange,
    loadSlaEditorData,
} from "../slaCompareEditorUtils";
import {formatSlaEditorTitle} from "../slaCompareMonthUtils";
import {CompareManualInputView} from "./CompareManualInputView";
import "../../../../../components/detailLayout/detailListLayout.css";
import "../slaCompare.css";

function CompareEditorDialog({open, mode, month, monthLabel, onClose, onSaved}) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState("config");
    const [children, setChildren] = useState([]);
    const [item, setItem] = useState([]);

    useEffect(() => {
        if (!open) {
            return;
        }
        document.body.classList.add("sla-compare-modal-open");
        return () => {
            document.body.classList.remove("sla-compare-modal-open");
        };
    }, [open]);

    useEffect(() => {
        if (!open || !month) {
            return;
        }

        let cancelled = false;
        setLoading(true);
        setStep("config");
        setChildren([]);
        setItem([]);

        loadSlaEditorData(month, mode)
            .then((result) => {
                if (cancelled) {
                    return;
                }
                setItem(result.items);
                setChildren(result.children);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, month, mode]);

    const weightSum = useMemo(() => computeWeightSum(children, item), [children, item]);

    const handleCheckRow = (idx) => {
        setChildren((prevChildren) =>
            prevChildren.map((child, childIndex) =>
                childIndex === idx ? {...child, checked: !child.checked} : child,
            ),
        );
    };

    const handleProceedToManual = () => {
        if (weightSum !== 100) {
            const proceed = window.confirm("가중치 합계가 100이 아닙니다. 그래도 수기입력으로 진행하시겠습니까?");
            if (!proceed) {
                return;
            }
        }

        const {children: calcChildren, items: calcItems} = applyWeightCalculations(children, item);
        setChildren(calcChildren);
        setItem(calcItems);
        setStep("manual");
    };

    const handleSave = async () => {
        setSaving(true);
        dispatch(apiLoading(true));

        try {
            const {children: calcChildren, items: calcItems} = applyWeightCalculations(children, item);
            const evaluationResult = computeEvaluationResult(calcChildren, calcItems);
            const payload = buildSavePayload(month, calcChildren, calcItems, evaluationResult);
            const response = mode === "update" ? await updateSla(payload) : await saveSla(payload);

            if (response?.status === 200) {
                handleActiveToast({message: mode === "update" ? "수정되었습니다." : "등록되었습니다."});
                onSaved?.();
                onClose?.();
            }
        } finally {
            setSaving(false);
            dispatch(apiLoading(false));
        }
    };

    const title = useMemo(() => {
        const base = formatSlaEditorTitle(month, monthLabel);
        return step === "manual" ? `${base} 수기입력` : base;
    }, [month, monthLabel, step]);

    const modalClassName = step === "manual"
        ? "sla-compare-modal sla-compare-modal--manual !flex !flex-col !max-w-none !p-0 !gap-0"
        : "sla-compare-modal !flex !flex-col !max-w-none !p-0 !gap-0";

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose?.()}>
            <DialogContent className={modalClassName}>
                <div className="sla-compare-modal__header">
                    <div className="sla-compare-modal__head-row">
                        <DialogTitle className="sla-compare-modal__title">{title}</DialogTitle>
                        <div className="sla-compare-modal__head-actions">
                            {step === "manual" && (
                                <DetailSummaryButton muted disabled={saving} onClick={() => setStep("config")}>
                                    이전
                                </DetailSummaryButton>
                            )}
                            <DetailSummaryButton muted disabled={saving} onClick={onClose}>
                                취소
                            </DetailSummaryButton>
                            {step === "config" ? (
                                <DetailSummaryButton
                                    disabled={loading || saving || children.length === 0}
                                    onClick={handleProceedToManual}
                                >
                                    수기입력
                                </DetailSummaryButton>
                            ) : (
                                <DetailSummaryButton disabled={saving} onClick={handleSave}>
                                    {saving ? "저장 중…" : "저장하기"}
                                </DetailSummaryButton>
                            )}
                        </div>
                    </div>
                </div>

                <div className="sla-compare-modal__body">
                    {loading ? (
                        <div className="sla-compare-modal__loading">불러오는 중…</div>
                    ) : step === "manual" ? (
                        <CompareManualInputView item={item} setItem={setItem} />
                    ) : (
                        <>
                            <div className="sla-compare-modal__toolbar">
                                <span className="sla-compare-modal__stat">
                                    가중치 합계{" "}
                                    <strong className={weightSum !== 100 ? "is-warning" : ""}>{weightSum}</strong>
                                    {" / 100"}
                                </span>
                                <span className="sla-compare-modal__stat-hint">
                                    동일 장애·산출물·성능개선·내부자료 유출 제외
                                </span>
                            </div>

                            <div className="sla-compare-modal__table-wrap">
                                <table className="sla-compare-editor-table w-full">
                                    <colgroup>
                                        <col className="sla-compare-editor-col--group"/>
                                        <col className="sla-compare-editor-col--name"/>
                                        <col className="sla-compare-editor-col--num"/>
                                        <col className="sla-compare-editor-col--num"/>
                                        <col className="sla-compare-editor-col--num"/>
                                    </colgroup>
                                    <thead>
                                    <tr>
                                        <th className="fieldset-th" colSpan={2} rowSpan={2}>측정지표 (그룹코드)</th>
                                        <th className="fieldset-th" rowSpan={2}>가중치(%)</th>
                                        <th className="fieldset-th" colSpan={2}>서비스 목표수준</th>
                                    </tr>
                                    <tr>
                                        <th className="fieldset-th">목표수준(%)</th>
                                        <th className="fieldset-th">최소수준(%)</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <th className="fieldset-th sla-compare-editor-table__group-label" rowSpan={children.length + 1}>
                                            가동률
                                        </th>
                                    </tr>
                                    {children.map((each, index) => (
                                        <tr key={`child-${each.id ?? index}`}>
                                            <td
                                                className={`fieldset-td sla-compare-editor-table__check-cell ${each.checked ? "" : "is-muted"}`}
                                                onClick={() => handleCheckRow(index)}
                                            >
                                                <label className="sla-compare-editor-table__check-label">
                                                    <input
                                                        type="checkbox"
                                                        className="sla-compare-modal__checkbox"
                                                        checked={each.checked}
                                                        onChange={(e) => {
                                                            handleSlaInputChange(setChildren, e.target.checked, index, "checked", "children");
                                                        }}
                                                    />
                                                    <span>{each.name}</span>
                                                </label>
                                            </td>
                                            <td className={`fieldset-td ${each.checked ? "" : "is-muted"}`}>
                                                <input
                                                    type="text"
                                                    className="basic-input"
                                                    value={each.weight ?? 10}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^-?\d*$/.test(value)) {
                                                            handleSlaInputChange(setChildren, value, index, "weight", "children");
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className={`fieldset-td ${each.checked ? "" : "is-muted"}`}>
                                                <input
                                                    type="text"
                                                    className="basic-input"
                                                    value={each.goal ?? 98}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^-?\d*$/.test(value)) {
                                                            handleSlaInputChange(setChildren, value, index, "goal", "children");
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className={`fieldset-td ${each.checked ? "" : "is-muted"}`}>
                                                <input
                                                    type="text"
                                                    className="basic-input"
                                                    value={each.min ?? 95}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^-?\d*$/.test(value)) {
                                                            handleSlaInputChange(setChildren, value, index, "min", "children");
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}

                                    {item.map((each, index) => (
                                        <tr key={`item-${each.section}`}>
                                            <th colSpan={2} className="fieldset-th">{each.title}</th>
                                            <td className="fieldset-td">
                                                <input
                                                    type="text"
                                                    className="basic-input"
                                                    value={each.weight}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^-?\d*$/.test(value)) {
                                                            handleSlaInputChange(setItem, value, index, "weight", "item");
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className="fieldset-td">
                                                <input
                                                    type="text"
                                                    className="basic-input"
                                                    value={each.goal}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^-?\d*$/.test(value)) {
                                                            handleSlaInputChange(setItem, value, index, "goal", "item");
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className="fieldset-td">
                                                <input
                                                    type="text"
                                                    className="basic-input"
                                                    value={each.min}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^-?\d*$/.test(value)) {
                                                            handleSlaInputChange(setItem, value, index, "min", "item");
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}

                                    <tr className="sla-compare-editor-table__sum-row">
                                        <th colSpan={2} className="fieldset-th">
                                            가중치 총합
                                        </th>
                                        <td className="fieldset-td">
                                            <input
                                                readOnly
                                                type="text"
                                                className={`basic-input sla-compare-modal__sum${weightSum !== 100 ? " is-warning" : ""}`}
                                                value={weightSum}
                                            />
                                        </td>
                                        <td className="fieldset-td" colSpan={2} />
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export {CompareEditorDialog};
